import { useCallback, useEffect, useState } from "react";

async function api(path, options) {
  return fetch(path, { credentials: "same-origin", ...options });
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function HandoverChecklist({ categories, items }) {
  return (
    <div className="section">
      <div className="section-head">
        <h3>Handover checklist</h3>
        <span className="muted" style={{ fontSize: 13 }}>
          What a new committee should inherit — add something under each heading.
        </span>
      </div>
      <div className="stat-grid">
        {categories.map((cat) => {
          const count = items.filter((i) => i.category === cat).length;
          return (
            <div key={cat} className="stat" style={count ? {} : { opacity: 0.55 }}>
              <div className="n" style={{ fontSize: 20, color: count ? "var(--green)" : "var(--muted)" }}>
                {count ? `${count} item${count === 1 ? "" : "s"}` : "Missing"}
              </div>
              <div className="l">{cat}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AddItemForm({ chapterId, categories, onAdded }) {
  const empty = { title: "", category: categories[0] || "", kind: "file", url: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    let res;
    if (form.kind === "file") {
      if (!file) {
        setError("Choose a file to upload.");
        setBusy(false);
        return;
      }
      const body = new FormData();
      body.append("file", file);
      body.append("title", form.title);
      body.append("category", form.category);
      body.append("notes", form.notes);
      res = await api(`/api/chapters/${chapterId}/vault`, { method: "POST", body });
    } else {
      res = await api(`/api/chapters/${chapterId}/vault`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
    }
    setBusy(false);
    if (res.ok) {
      setForm(empty);
      setFile(null);
      e.target.reset();
      onAdded();
    } else {
      setError((await res.json().catch(() => ({}))).error || "Could not add the item.");
    }
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">
          Type
          <select name="kind" value={form.kind} onChange={set("kind")}>
            <option value="file">File upload</option>
            <option value="link">External link</option>
          </select>
        </label>
        {form.kind === "file" ? (
          <label className="field">
            File
            <input type="file" name="file" onChange={(e) => setFile(e.target.files[0] || null)} />
          </label>
        ) : (
          <label className="field">
            URL
            <input name="url" required value={form.url} onChange={set("url")} placeholder="https://…" />
          </label>
        )}
        <label className="field">
          Title
          <input
            name="title"
            value={form.title}
            onChange={set("title")}
            placeholder={form.kind === "file" ? "Defaults to file name" : "Chapter bylaws"}
            required={form.kind === "link"}
          />
        </label>
        <label className="field">
          Category
          <input name="category" list="vault-categories" value={form.category} onChange={set("category")} />
          <datalist id="vault-categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>
        <label className="field">
          Notes
          <input name="notes" value={form.notes} onChange={set("notes")} placeholder="Context for your successor" />
        </label>
        <button className="btn-primary" style={{ width: "auto" }} type="submit" disabled={busy}>
          {busy ? "Adding…" : "Add to bucket"}
        </button>
      </div>
      {error && <div className="err">{error}</div>}
    </form>
  );
}

function EditRow({ item, onDone }) {
  const [form, setForm] = useState({
    title: item.title,
    category: item.category || "",
    url: item.url || "",
    notes: item.notes || "",
  });
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save(e) {
    e.preventDefault();
    const res = await api(`/api/vault/${item.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (res.ok) onDone(true);
    else setError((await res.json().catch(() => ({}))).error || "Could not save.");
  }

  return (
    <form className="form-card" onSubmit={save} style={{ marginTop: 0 }}>
      <div className="form-grid">
        <label className="field">
          Title
          <input name="title" required value={form.title} onChange={set("title")} />
        </label>
        <label className="field">
          Category
          <input name="category" list="vault-categories" value={form.category} onChange={set("category")} />
        </label>
        {item.kind === "link" && (
          <label className="field">
            URL
            <input name="url" required value={form.url} onChange={set("url")} />
          </label>
        )}
        <label className="field">
          Notes
          <input name="notes" value={form.notes} onChange={set("notes")} />
        </label>
        <div className="field-actions">
          <button className="btn-primary" style={{ width: "auto" }} type="submit">
            Save
          </button>
          <button className="btn-ghost" type="button" onClick={() => onDone(false)}>
            Cancel
          </button>
        </div>
      </div>
      {error && <div className="err">{error}</div>}
    </form>
  );
}

export function ResourceVault({ chapterId, canManage }) {
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    const res = await api(`/api/chapters/${chapterId}/vault`);
    if (res.ok) {
      const data = await res.json();
      setItems(data.items);
      setCategories(data.categories);
    }
  }, [chapterId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function remove(item) {
    if (!window.confirm(`Remove "${item.title}" from the bucket?`)) return;
    await api(`/api/vault/${item.id}`, { method: "DELETE" });
    reload();
  }

  if (items === null) return <p className="muted">Loading…</p>;

  const grouped = new Map();
  for (const item of items) {
    const key = item.category || "Uncategorised";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(item);
  }

  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        The chapter's private vault — bylaws, runbooks, templates, reports and access
        notes, so an incoming committee inherits everything. Visible to officers only.
      </p>

      <HandoverChecklist categories={categories} items={items} />

      {canManage && (
        <div className="section">
          <div className="section-head">
            <h3>Add an item</h3>
          </div>
          <AddItemForm chapterId={chapterId} categories={categories} onAdded={reload} />
        </div>
      )}

      {items.length === 0 ? (
        <p className="empty">The bucket is empty — add the chapter's key documents above.</p>
      ) : (
        [...grouped.entries()].map(([category, group]) => (
          <div className="section" key={category}>
            <div className="section-head">
              <h3>{category}</h3>
            </div>
            <table className="tbl">
              <tbody>
                {group.map((item) =>
                  editing === item.id ? (
                    <tr key={item.id}>
                      <td colSpan={canManage ? 3 : 2}>
                        <EditRow
                          item={item}
                          onDone={(saved) => {
                            setEditing(null);
                            if (saved) reload();
                          }}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={item.id}>
                      <td>
                        {item.kind === "file" ? (
                          <a href={`/api/vault/${item.id}/download`}>{item.title}</a>
                        ) : (
                          <a href={item.url} target="_blank" rel="noreferrer">
                            {item.title}
                          </a>
                        )}
                        {item.notes && (
                          <div className="muted" style={{ fontSize: 13 }}>
                            {item.notes}
                          </div>
                        )}
                      </td>
                      <td className="muted" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                        {item.kind === "file"
                          ? `${item.file_name} · ${formatSize(item.file_size)}`
                          : "link"}
                        {item.added_by && <div>added by {item.added_by}</div>}
                      </td>
                      {canManage && (
                        <td className="row-actions">
                          <button className="btn-ghost" onClick={() => setEditing(item.id)} type="button">
                            Edit
                          </button>
                          <button className="btn-danger" onClick={() => remove(item)} type="button">
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        ))
      )}
    </>
  );
}
